import React, { useState } from 'react';
import './PayoutModal.css';

// Used to render historical (`Manu_Profiles.payout_method`) values readably
// even though new payouts always go out as 'wisePay'.
const PAYOUT_METHOD_LABELS = {
  wireTransfer: 'Wire Transfer',
  paypal: 'PayPal',
  wisePay: 'Wise',
  ria: 'RIA Money Transfer',
  moneyGram: 'MoneyGram',
  remitly: 'Remitly'
};

const PayoutModal = ({ payout, payoutInfo, onClose, onSubmit }) => {
  const [referenceNumber, setReferenceNumber] = useState('');
  const [status, setStatus] = useState('sent');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const payoutLabel = payout.payout_number === 1 ? '70%' : '30%';
  const methodLabel = payoutInfo?.payout_method
    ? (PAYOUT_METHOD_LABELS[payoutInfo.payout_method] || payoutInfo.payout_method)
    : 'Not set';

  // Known field keys → admin-friendly label. Covers the Wise schema
  // (account holder + bank fields + address.*) and legacy method fields
  // (fullName, recipientName, IBAN, etc.). Anything not in this map gets
  // a fallback label derived from the key itself.
  const FIELD_LABELS = {
    accountHolderName: 'Account Holder Name',
    accountHolderNameChinese: 'Account Holder (Chinese)',
    accountHolderNameKatakana: 'Account Holder (Katakana)',
    fullName: 'Full Name',
    accountName: 'Account Name',
    recipientName: 'Recipient Name',
    email: 'Email',
    phone: 'Phone',
    phoneNumber: 'Phone Number',
    dateOfBirth: 'Date of Birth',
    businessRegistrationNumber: 'Business Registration #',
    recipientType: 'Recipient Type',
    country: 'Country',
    countryName: 'Country',
    currency: 'Currency',
    bankName: 'Bank Name',
    branchName: 'Branch Name',
    bankBranch: 'Bank Branch',
    iban: 'IBAN',
    IBAN: 'IBAN',
    accountNumber: 'Account Number',
    routingNumber: 'Routing Number',
    swiftCode: 'SWIFT / BIC',
    bankCode: 'Bank Code',
    accountType: 'Account Type',
    deliveryMethod: 'Delivery Method',
    'address.line1': 'Address Line 1',
    'address.line2': 'Address Line 2',
    'address.city': 'City',
    'address.state': 'State / Province',
    'address.postalCode': 'Postal Code',
    'address.country': 'Country',
  };

  const humanizeKey = (key) =>
    key
      .replace(/^address\./, '')
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (c) => c.toUpperCase())
      .trim();

  const getPayoutDetails = () => {
    if (!payoutInfo?.payment_details) return [];
    const d = payoutInfo.payment_details;
    const rows = [];

    for (const [key, val] of Object.entries(d)) {
      if (val === null || val === undefined || val === '') continue;
      if (key === 'address' && typeof val === 'object') {
        for (const [aKey, aVal] of Object.entries(val)) {
          if (!aVal) continue;
          const fullKey = `address.${aKey}`;
          rows.push({ label: FIELD_LABELS[fullKey] || humanizeKey(aKey), value: String(aVal) });
        }
      } else if (typeof val !== 'object') {
        rows.push({ label: FIELD_LABELS[key] || humanizeKey(key), value: String(val) });
      }
    }
    return rows;
  };

  const [copiedKey, setCopiedKey] = useState(null);
  const copyValue = async (label, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(label);
      setTimeout(() => setCopiedKey((current) => (current === label ? null : current)), 1500);
    } catch (err) {
      console.error('Copy failed', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!referenceNumber.trim()) {
      setError('Reference number is required');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Backend always records as 'wisePay' regardless of input — admin no
      // longer picks a method here.
      await onSubmit(payout.id, {
        referenceNumber: referenceNumber.trim(),
        status,
        notes: notes.trim() || undefined
      });
    } catch (err) {
      setError(err.message);
      setIsSubmitting(false);
    }
  };

  const payoutDetails = getPayoutDetails();

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Record Payout</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        {/* Payout Info Summary */}
        <div className="modal-info">
          <div className="modal-info-row">
            <span>Quote</span>
            <span className="modal-info-value">{payout.quote_number || '—'}</span>
          </div>
          <div className="modal-info-row">
            <span>Manufacturer</span>
            <span className="modal-info-value">{payout.profiles?.full_name || '—'}</span>
          </div>
          <div className="modal-info-row">
            <span>Client</span>
            <span className="modal-info-value">{payout.client?.full_name || '—'}</span>
          </div>
          <div className="modal-info-row">
            <span>Type</span>
            <span className="modal-info-value">{payoutLabel} Payout</span>
          </div>
          <div className="modal-info-row">
            <span>Amount</span>
            <span className="modal-info-value modal-amount">${Number(payout.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
          </div>
          {payout.payout_ref && (
            <div className="modal-info-row">
              <span>Tchpack Ref</span>
              <span className="modal-info-value modal-ref">{payout.payout_ref}</span>
            </div>
          )}
        </div>

        {/* Manufacturer Payout Details */}
        <div className="modal-payout-info">
          <h4>Manufacturer Payout Info</h4>
          <div className="modal-payout-method">
            <span className="modal-payout-method-label">Preferred: {methodLabel}</span>
          </div>
          {payoutDetails.length > 0 ? (
            <div className="modal-payout-details">
              {payoutDetails.map((d) => (
                <div key={d.label} className="modal-detail-row">
                  <span className="modal-detail-label">{d.label}</span>
                  <span className="modal-detail-value-wrap">
                    <span className="modal-detail-value">{d.value}</span>
                    <button
                      type="button"
                      className={`modal-copy-btn ${copiedKey === d.label ? 'copied' : ''}`}
                      onClick={() => copyValue(d.label, d.value)}
                      title={`Copy ${d.label}`}
                      aria-label={`Copy ${d.label}`}
                    >
                      {copiedKey === d.label ? (
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      ) : (
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                      )}
                    </button>
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="modal-no-details">No saved payout details for this manufacturer.</p>
          )}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="modal-field">
            <label>Transfer Reference Number *</label>
            <input
              type="text"
              placeholder="e.g. R23127979536"
              value={referenceNumber}
              onChange={(e) => setReferenceNumber(e.target.value)}
              autoFocus
            />
          </div>

          <div className="modal-field">
            <label>Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="sent">Sent</option>
              <option value="completed">Completed</option>
            </select>
          </div>

          <div className="modal-field">
            <label>Notes (optional)</label>
            <textarea
              placeholder="Any additional notes..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>

          {error && <div className="modal-error">{error}</div>}

          <div className="modal-actions">
            <button type="button" className="modal-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="modal-submit" disabled={isSubmitting}>
              {isSubmitting ? 'Recording...' : 'Record Payout'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PayoutModal;
